import { useMemo, useState } from 'react'
import { message as $message } from 'antd'
import type { CommissionRule } from '@/services/partnership'
import {
    getCommissionRuleList,
    partnerNeedPayDetails,
    specialRuleDetail,
    partnerRuleModify,
    queryMyConfig,
    ruleModifyV2,
} from '@/services/partnership'
import { getCoIsOpenPay } from '@/services/onlinePayment/pay'
import ReactDOM from 'react-dom'

export interface CashBackContent {
    /**
     * @description       交易金额
     */
    tradeAmountLimit?: number
    /**
     * @description       一次性奖励
     */
    commissionAmount?: number
}

export interface PercentageBackContent {
    /**
     * @description       佣金比例
     */
    commissionRatio?: number
    /**
     * @description       有效期 - 月
     */
    validMonth?: number
    /**
     * @description       最高佣金
     */
    commissionMax?: number
}

export interface OtherInfoContent {
    /**
     * @description       合伙伙伴coId
     */
    partnerCoId?: string
    /**
     * @description       合伙伙伴等级
     */
    partnerLevel?: string
    /**
     * @description       专属规则标志
     */
    specialRuleFlag?: boolean
}

export interface updataSpecialContent {
    /**
     * @description       合伙伙伴coId
     */
    partnerCoId?: string
    /**
     * @description       合伙伙伴等级
     */
    partnerLevel?: string
    /**
     * @description       专属规则标志
     */
    specialRuleFlag?: boolean
    /**
     * @description       专属规则
     */
    ruleVoList?: Array<CommissionRule<CashBackContent | PercentageBackContent>>
}

export const CASH_BACK = 'CASH_BACK'
export const PERCENTAGE = 'PERCENTAGE'

/*
 * 合伙人 相关
 * */
export default function usePartnership() {
    // 返现
    const [cashBack, setCashBack] = useState<CommissionRule<CashBackContent>>({
        enabled: false,
        ruleType: CASH_BACK,
        ruleContent: {},
    })
    // 专属返现
    const [specialCashBack, setSpecialCashBack] = useState<CommissionRule<CashBackContent>>({
        enabled: false,
        ruleType: CASH_BACK,
        ruleContent: {},
    })

    // 抽佣
    const [percentage, setPercentage] = useState<CommissionRule<PercentageBackContent>>({
        enabled: false,
        ruleType: PERCENTAGE,
        ruleContent: {},
    })

    // 专属抽佣
    const [specialPercentage, setSpecialPercentage] = useState<CommissionRule<PercentageBackContent>>({
        enabled: false,
        ruleType: PERCENTAGE,
        ruleContent: {},
    })

    // 专属抽佣
    const [specialOtherInfo, setSpecialOtherInfo] = useState<OtherInfoContent>({
        partnerCoId: '',
        partnerLevel: '',
        specialRuleFlag: false,
    })

    const [loading, setLoading] = useState(true)

    // 规则未创建过
    const [emptyRule, setEmptyRule] = useState(true)

    // 开通在线支付
    const [isOpen, setIsOpen] = useState(true)

    // 开通自然人在线收款 是否允许收款
    const [supportCollection, setSupportCollection] = useState(false)

    // 一键公开招募合伙人
    const [makePublic, setMakePublic] = useState(false)

    // 一键公开招募合伙人loading
    const [makePublicLoading, setMakePublicLoading] = useState(false)

    // 待支付佣金合伙人数量
    const [unpaidCount, setUnpaidCount] = useState(0)

    // 规则都未启用
    const noEnabled = useMemo(() => {
        return !cashBack?.enabled && !percentage?.enabled
    }, [cashBack, percentage])

    // 查询佣金规则信息
    const queryRules = async () => {
        try {
            const { success, message, data } = await getCommissionRuleList()
            if (success) {
                if (data?.length > 0) {
                    setEmptyRule(false)
                }
                data?.forEach(item => {
                    const ruleContent = JSON.parse(item.ruleContent ?? '{}')
                    if (item.ruleType === CASH_BACK) {
                        setCashBack({ ...item, ruleContent })
                    }
                    if (item.ruleType === PERCENTAGE) {
                        setPercentage({ ...item, ruleContent })
                    }
                })
            } else {
                $message.error(message)
            }
        } catch (e) {
            console.error('catchError', e)
        }
    }

    // 当前合伙人是否一键公开招募合伙人
    const queryMyMakePublicConfig = async () => {
        setMakePublicLoading(true)
        try {
            const { data, success, message } = await queryMyConfig()
            if (success) {
                setMakePublic(data.makePublic)
            } else {
                $message.error(message)
            }
        } catch (e) {
            console.error(e)
        }
        setMakePublicLoading(false)
    }

    // 查询特殊佣金规则信息
    const querySpecialRuleDetail = async (dataObj: any) => {
        try {
            setLoading(true)
            const { success, message, data } = await specialRuleDetail(dataObj)
            if (success) {
                console.log('data: ', data)
                const { ruleVoList, partnerCoId, partnerLevel, specialRuleFlag } = data
                console.log('partnerCoId, partnerLevel, specialRuleFlag: ', partnerCoId, partnerLevel, specialRuleFlag)
                if (ruleVoList?.length > 0) {
                    setEmptyRule(false)
                }
                setSpecialOtherInfo({
                    partnerCoId: partnerCoId,
                    partnerLevel: partnerLevel || '-1',
                    specialRuleFlag: specialRuleFlag,
                })
                ruleVoList?.forEach(item => {
                    const ruleContent = JSON.parse(item.ruleContent ?? '{}')
                    if (item.ruleType === CASH_BACK) {
                        setSpecialCashBack({ ...item, ruleContent })
                    }
                    if (item.ruleType === PERCENTAGE) {
                        setSpecialPercentage({ ...item, ruleContent })
                    }
                })
                if (!ruleVoList || ruleVoList.length === 0) {
                    setSpecialCashBack({
                        enabled: false,
                        ruleType: CASH_BACK,
                        ruleContent: {},
                    })
                    setSpecialPercentage({
                        enabled: false,
                        ruleType: PERCENTAGE,
                        ruleContent: {},
                    })
                }
                setLoading(false)
            } else {
                setLoading(false)
                $message.error(message)
            }
        } catch (e) {
            setLoading(false)
            console.error('catchError', e)
        }
    }

    // 保存佣金规则信息
    const handleRulesUpsert = async ({
        makePublic,
        ruleVoList,
    }: {
        makePublic: boolean
        ruleVoList: Array<CommissionRule<CashBackContent | PercentageBackContent>>
    }) => {
        const filterParam = ruleVoList.map(item => {
            return {
                ...item,
                ruleContent: JSON.stringify(item?.ruleContent ?? {}),
            }
        })
        const { success, message, data } = await ruleModifyV2({
            makePublic,
            ruleVoList: filterParam,
        })
        if (success && data) {
            queryRules()
            queryMyMakePublicConfig()
            $message.success('保存成功')
            return success
        }
        $message.error(message)
        return false
    }

    // 保存专属佣金规则信息
    const handleSpecialRulesUpsert = async (param: updataSpecialContent) => {
        let filterParam = []
        if (param.ruleVoList?.length) {
            filterParam = param.ruleVoList.map(item => {
                return {
                    ...item,
                    ruleContent: JSON.stringify(item?.ruleContent ?? {}),
                }
            })
        }
        const { success, message, data } = await partnerRuleModify({ ...param, ruleVoList: filterParam })
        if (success && data) {
            $message.success('保存成功')
            return success
        }
        $message.error(message)
        return false
    }

    // 当前合伙人是否开通在线支付
    const isOnlinePayOpen = async () => {
        try {
            const { data, success } = await getCoIsOpenPay()
            if (success) {
                ReactDOM.unstable_batchedUpdates(() => {
                    setIsOpen(data?.isOpen)
                    setSupportCollection(data?.supportCollection)
                })
            } else {
                console.error('获取是否开通在线支付出错')
            }
        } catch (e) {
            console.error(e)
        }
    }

    const updateMakePublicConfig = async (makePublic: boolean) => {
        setMakePublicLoading(true)
        try {
            const { success, message } = await ruleModifyV2({ makePublic })
            if (success) {
                setMakePublic(makePublic)
            } else {
                $message.error(message)
            }
        } catch (e) {
            console.error(e)
        }
        setMakePublicLoading(false)
    }

    // 待支付佣金合伙人数量
    const getPartnerNeedPayDetails = async () => {
        if (emptyRule) {
            return
        }
        try {
            const { data, success, message } = await partnerNeedPayDetails()
            if (success) {
                setUnpaidCount(data?.needPayCount)

                return data?.needPayCount
            } else {
                console.error(message)
            }
        } catch (e) {
            console.error(e)
        }
    }

    return {
        specialCashBack,
        specialOtherInfo,
        specialPercentage,
        supportCollection,
        setSpecialOtherInfo,
        emptyRule, // 未创建佣金规则
        noEnabled, // 规则都未启用
        cashBack,
        setCashBack,
        setSpecialCashBack,
        setSpecialPercentage,
        percentage,
        setPercentage,
        queryRules,
        handleRulesUpsert,
        isOpen,
        loading,
        isOnlinePayOpen,

        makePublic,
        makePublicLoading,
        queryMyMakePublicConfig,
        updateMakePublicConfig,

        unpaidCount,
        getPartnerNeedPayDetails,
        querySpecialRuleDetail,
        handleSpecialRulesUpsert,
    }
}
